import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { subscriptionService } from '@/services/subscriptionService';

interface SubscriptionStatusCardProps {
  showCancelDialog: boolean;
  setShowCancelDialog: React.Dispatch<React.SetStateAction<boolean>>;
  containerRef?: React.RefObject<HTMLDivElement>;
  subscription?: {
    id: string;
    id_stripe: string;
    status: string;
    has_active_subscription: boolean;
    current_period_end: string;
    final_amount_formatted: string;
    discount?: {
      coupon_code: string;
      discount_formatted: string;
    } | null;
  };
  isLoading?: boolean;
}

export const SubscriptionStatusCard: React.FC<SubscriptionStatusCardProps> = ({
  showCancelDialog,
  setShowCancelDialog,
  containerRef,
  subscription,
  isLoading
}) => {
  useLanguage();
  // Formata data do período atual para DD/MM/YYYY
  const renewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(getInterfaceLanguage())
    : '—';
  const [cancelStep, setCancelStep] = useState<'confirm' | 'success'>('confirm');
  const [countdown, setCountdown] = useState(5);

  // Reset states when dialog closes
  useEffect(() => {
    if (!showCancelDialog) {
      setCancelStep('confirm');
      setCountdown(5);
    }
  }, [showCancelDialog]);

  // Countdown logic
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (cancelStep === 'success' && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (cancelStep === 'success' && countdown === 0) {
      setShowCancelDialog(false);
      toast.success(translateInterfaceText("Assinatura cancelada com sucesso"), {
        description: translateInterfaceText("Sua assinatura foi cancelada com sucesso.")
      });
    }
    
    return () => clearTimeout(timer);
  }, [cancelStep, countdown, setShowCancelDialog]);

  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirmCancel = async () => {
    if (!subscription?.id_stripe) {
      toast.error(translateInterfaceText("ID da assinatura não encontrado."));
      return;
    }

    setIsCancelling(true);
    try {
      const response = await subscriptionService.cancelSub(subscription.id_stripe);
      if (response?.success === false) {
        toast.error(response.message || translateInterfaceText("Erro ao cancelar assinatura."));
        return;
      }
      setCancelStep('success');
    } catch {
      toast.error(translateInterfaceText("Erro inesperado ao cancelar assinatura."));
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Título "Assinatura" */}
      <h2 className="text-[16px] font-semibold text-[#111827]">
        {translateInterfaceText("Assinatura")}</h2>
      
      {/* Texto descritivo com data de renovação */}
      <p className="text-[14px] text-[#4B5563]">
        {translateInterfaceText("Sua assinatura está ativa e deve ser renovada na data de")}{" "}{renewalDate}.
      </p>
      
      {/* Linha divisória + Botão */}
      <div className="border-t border-[#E5E7EB] pt-4 mt-2">
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              className="h-9 px-5 border-[#FECACA] text-[#EF4444] text-[14px] font-semibold rounded-[10px] bg-white hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] transition-colors w-fit"
            >
              {translateInterfaceText("Cancelar Assinatura")}</Button>
          </DialogTrigger>
          <DialogContent 
            hideCloseButton
            className="p-0 overflow-hidden max-w-[520px] rounded-[18px] shadow-[0_12px_40px_rgba(0,0,0,0.14)] bg-white"
          >
            {/* Tela de confirmação */}
            <div className={`${cancelStep === 'confirm' ? 'block' : 'hidden'}`}>
              {/* Header com ícone centralizado */}
              <div className="pt-8 pb-4 px-6 text-center">
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                  <AlertTriangle className="h-7 w-7 text-[#D97706]" />
                </div>
                <h2 className="text-[20px] font-semibold text-[#111827]">
                  {translateInterfaceText("Tem certeza que deseja cancelar?")}</h2>
              </div>

              {/* Conteúdo */}
              <div className="px-6 pb-4">
                <div className="bg-[#FEF9E7] border border-[#FDE68A] rounded-[12px] p-4">
                  <p className="text-[14px] text-[#92400E] font-medium mb-3">
                    {translateInterfaceText("Ao cancelar sua assinatura:")}</p>
                  <ul className="space-y-2 text-[13px] text-[#92400E]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97706] mt-0.5">•</span>
                      {translateInterfaceText("Você perderá acesso a todos os recursos da HostVSL")}</li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97706] mt-0.5">•</span>
                      {translateInterfaceText("Seus vídeos não serão mais exibidos")}</li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97706] mt-0.5">•</span>
                      {translateInterfaceText("Seus dados serão mantidos por 30 dias")}</li>
                  </ul>
                </div>
                
                <p className="text-[13px] text-[#6B7280] mt-4 text-center">
                  {translateInterfaceText("Ao confirmar, sua assinatura será cancelada imediatamente e o acesso será encerrado.")}</p>
              </div>

              {/* Footer com botões */}
              <div className="px-6 pb-6 flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowCancelDialog(false)}
                  className="flex-1 h-[44px] border-[#D1D5DB] text-[#374151] rounded-[10px] font-medium"
                >
                  {translateInterfaceText("Voltar")}</Button>
                <Button
                  onClick={handleConfirmCancel}
                  disabled={isCancelling}
                  className="flex-1 h-[44px] bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-[10px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isCancelling ? translateInterfaceText("Cancelando...") : translateInterfaceText("Confirmar cancelamento")}
                </Button>
              </div>
            </div>

            {/* Tela de sucesso com contador */}
            <div className={`${cancelStep === 'success' ? 'block' : 'hidden'} py-10 px-6 text-center bg-white min-h-[340px]`}>
              {/* Ícone de sucesso animado */}
              <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-[#DCFCE7] flex items-center justify-center animate-scale-in">
                <CheckCircle2 className="h-9 w-9 text-[#16A34A]" />
              </div>
              
              <h2 className="text-[20px] font-semibold text-[#111827] mb-2">
                {translateInterfaceText("Cancelamento confirmado")}</h2>
              
              <p className="text-[14px] text-[#6B7280] mb-6">
                {translateInterfaceText("Sua assinatura será encerrada ao final do período atual.")}</p>
              
              {/* Contador circular */}
              <div className="relative mx-auto w-20 h-20 mb-6">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40" cy="40" r="36"
                    stroke="#E5E7EB" strokeWidth="6" fill="none"
                  />
                  <circle
                    cx="40" cy="40" r="36"
                    stroke="#16A34A" strokeWidth="6" fill="none"
                    strokeDasharray={226}
                    strokeDashoffset={226 - (226 * countdown / 5)}
                    className="transition-all duration-1000 ease-linear"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[28px] font-bold text-[#111827]">
                  {countdown}
                </span>
              </div>
              
              <p className="text-[13px] text-[#9CA3AF]">
                {translateInterfaceText("Fechando automaticamente...")}</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
